import { useQuery } from '@tanstack/react-query';
import { axiosClient } from '@/lib/axiosClient';
import type { EventGuest } from '@/services/eventGuestService';

export interface PaginatorInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface ConventionReportSummary {
  status: string;
  period: { period_id: number; name: string };
  generated_at: string;
  participants: {
    total: number;
    paid: number;
    unpaid: number;
    pending: number;
    total_revenue: number;
    total_revenue_paid: number;
    total_revenue_pending: number;
    total_revenue_unpaid: number;
    per_package: { package_id: number; package_name: string; total: number; paid: number; unpaid: number; pending: number; revenue_paid: number }[];
    per_institution: { institution: string; total: number; paid: number; unpaid: number; revenue_paid: number; with_rfid: number; is_printed: number; breakdown?: { category: string; total: number; paid: number; unpaid: number; revenue_paid: number; with_rfid: number; is_printed: number; }[] }[];
    per_gender: { male: number; female: number; unknown: number };
    per_clothing_size: Record<string, number>;
    per_blood_type: Record<string, number>;
    with_rfid: number;
    without_rfid: number;
    is_printed: number;
    not_printed: number;
    per_registration_type: { individual: number; pic: number };
  };
  event_guests: {
    total: number;
    per_category: { id: number; name: string; color_hex: string; total: number; with_rfid: number; is_printed: number; is_emailed: number }[];
    per_institution: { institution: string; total: number; with_rfid: number; is_printed: number; breakdown?: { category: string; count: number; with_rfid: number; is_printed: number }[] }[];
    per_gender: { male: number; female: number; unknown: number };
    per_clothing_size: Record<string, number>;
    per_blood_type: Record<string, number>;
    with_rfid: number;
    without_rfid: number;
    is_printed: number;
    not_printed: number;
    is_emailed: number;
    not_emailed: number;
    has_email: number;
    no_email: number;
  };
}

export const useConventionReport = (periodId: number) => {
  return useQuery<ConventionReportSummary>({
    queryKey: ['convention-report', 'summary', periodId],
    queryFn: async () => {
      if (!periodId) return null;
      const { data } = await axiosClient.get(`/periods/${periodId}/convention-report`);
      return data;
    },
    enabled: !!periodId,
  });
};

export interface ParticipantListFilters {
  page?: number;
  per_page?: number;
  search?: string;
  package_id?: string;
  payment_status?: string;
  institution?: string;
  gender?: string;
  blood_type?: string;
  clothing_size?: string;
  rfid_status?: string;
  is_printed?: string;
}

export interface AdminParticipantRegistration {
  registration_id: number;
  period_id: number;
  package_id: number;
  pic_id?: number | null;
  rfid_number?: string | null;
  is_printed?: boolean;
  participant?: {
    full_name: string;
    institution?: string;
    gender?: string;
  };
  package?: { name: string; price: number };
  latest_payment?: { status: string; amount: number; invoice_number?: string };
  payment_status?: string;
}

export const useConventionReportParticipants = (periodId: number, filters: ParticipantListFilters) => {
  return useQuery<{ status: string; data: AdminParticipantRegistration[]; meta: PaginatorInfo }>({
    queryKey: ['convention-report', 'participants', periodId, filters],
    queryFn: async () => {
      if (!periodId) return null;
      const { data } = await axiosClient.get(`/periods/${periodId}/convention-report/participants`, { params: filters });
      return data;
    },
    enabled: !!periodId,
  });
};

export interface GuestListFilters {
  page?: number;
  per_page?: number;
  search?: string;
  card_category_id?: string;
  institution?: string;
  gender?: string;
  blood_type?: string;
  clothing_size?: string;
  rfid_status?: string;
  is_printed?: string;
  is_emailed?: string;
  has_email?: string;
}

export const useConventionReportGuests = (periodId: number, filters: GuestListFilters) => {
  return useQuery<{ status: string; data: EventGuest[]; meta: PaginatorInfo }>({
    queryKey: ['convention-report', 'guests', periodId, filters],
    queryFn: async () => {
      if (!periodId) return null;
      const { data } = await axiosClient.get(`/periods/${periodId}/convention-report/guests`, { params: filters });
      return data;
    },
    enabled: !!periodId,
  });
};
